import db from './db';

// ##### HELPER: Delete one IndexedDB database by name
// Resolves on "blocked" too: the browser finishes the delete once open connections close
// (e.g. the service worker's workbox-expiration DB after unregister), so we don't hang here.
function deleteIndexedDB(name) {
  return new Promise((resolve) => {
    try {
      const request = indexedDB.deleteDatabase(name);
      request.onsuccess = () => resolve();
      request.onerror = () => resolve();
      request.onblocked = () => resolve();
    } catch (error) {
      resolve();
    }
  });
}

// ##### WIPE OFFLINE DATA - Only the app's Dexie DB (QAppDB)
// Used when a different user logs in on this browser. Reopens the DB empty so the app keeps working.
export async function wipeOfflineData() {
  try {
    await db.delete();
    await db.open();
  } catch (error) {
    console.error('Failed to wipe offline data:', error);
  }
}

// ##### WIPE ALL LOCAL DATA - Everything this app stores in the browser
// Service worker, Cache Storage, every IndexedDB of this origin, localStorage and sessionStorage.
// Each step is independent: a failure in one never skips the others.
export async function wipeAllLocalData() {
  // 1. Service worker (unregister first so it releases its caches and IndexedDB)
  try {
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map((registration) => registration.unregister()));
    }
  } catch (error) {
    console.error('Failed to unregister service workers:', error);
  }

  // 2. Cache Storage (HTML, scripts and images cached by the service worker)
  try {
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      await Promise.all(cacheNames.map((name) => caches.delete(name)));
    }
  } catch (error) {
    console.error('Failed to clear Cache Storage:', error);
  }

  // 3. IndexedDB (QAppDB + any other DB of this origin, e.g. workbox-expiration)
  try {
    db.close();
  } catch (error) {
    // Already closed
  }
  const dbNames = new Set([db.name]);
  try {
    if (typeof indexedDB.databases === 'function') {
      const databases = await indexedDB.databases();
      databases.forEach((database) => database.name && dbNames.add(database.name));
    }
  } catch (error) {
    console.error('Failed to list IndexedDB databases:', error);
  }
  await Promise.all([...dbNames].map(deleteIndexedDB));

  // 4. Web storage (tokens, cached user, UI preferences)
  try {
    localStorage.clear();
  } catch (error) {
    console.error('Failed to clear localStorage:', error);
  }
  try {
    sessionStorage.clear();
  } catch (error) {
    console.error('Failed to clear sessionStorage:', error);
  }
}
